import type { EnvironmentId, ProjectId, PullRequestChecksState } from "@afar-research/contracts";
import { useState } from "react";

import { usePullRequestChecks } from "../../state/pullRequests";
import { Menu, MenuGroup, MenuGroupLabel, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import {
  PullRequestCheckStatusIcon,
  pullRequestCheckStatusLabel,
  pullRequestChecksStatePresentation,
} from "./pullRequestPresentation";

/**
 * The rollup a row carries, opening into the runs behind it.
 *
 * Following T3Code, the body is mounted only while the menu is open, so a page of rows does not
 * each read a hundred check runs for a reader who looks at one.
 */

function ChecksBody({
  environmentId,
  projectId,
  host,
  repository,
  number,
}: {
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId;
  readonly host: string | undefined;
  readonly repository: string;
  readonly number: number;
}) {
  const query = usePullRequestChecks({
    environmentId,
    input: { projectId, ...(host === undefined ? {} : { host }), repository, number },
  });

  if (query.error !== null) {
    return <MenuGroupLabel>The checks could not be read. {query.error}</MenuGroupLabel>;
  }
  if (query.data === null) {
    return (
      <MenuGroupLabel>{query.isPending ? "Reading checks…" : "No checks read."}</MenuGroupLabel>
    );
  }
  if (query.data.status === "unavailable") {
    return <MenuGroupLabel>{query.data.failure.message}</MenuGroupLabel>;
  }
  // An answer about another pull request is not an answer about this one.
  if (query.data.repository !== repository || query.data.number !== number) {
    return (
      <MenuGroupLabel>The Environment answered about a different pull request.</MenuGroupLabel>
    );
  }
  if (query.data.checks.length === 0) {
    return <MenuGroupLabel>No checks reported.</MenuGroupLabel>;
  }
  return (
    <>
      {query.data.checks.map((check, index) => (
        // The host decides how many runs share a name, so position separates them: a repeated
        // key would be a rendering fault on top of whatever the list already says.
        <MenuItem
          key={`${index}:${check.name}:${check.url ?? ""}`}
          {...(check.url === null
            ? {}
            : { onClick: () => window.open(check.url ?? "", "_blank", "noreferrer") })}
        >
          <span className="flex min-w-0 items-center gap-2 text-xs">
            <PullRequestCheckStatusIcon status={check.status} />
            <span className="min-w-0 flex-1 truncate">{check.name}</span>
            <span className="shrink-0 text-muted-foreground">
              {pullRequestCheckStatusLabel(check.status)}
            </span>
          </span>
        </MenuItem>
      ))}
    </>
  );
}

export function PullRequestChecksPopover({
  state,
  environmentId,
  projectId,
  host,
  repository,
  number,
}: {
  readonly state: PullRequestChecksState;
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId;
  readonly host: string | undefined;
  readonly repository: string;
  readonly number: number;
}) {
  const [open, setOpen] = useState(false);
  const presentation = pullRequestChecksStatePresentation(state);
  return (
    <Menu open={open} onOpenChange={setOpen}>
      <MenuTrigger
        render={
          <button
            aria-label={`${presentation.label}. Show the checks.`}
            className="inline-flex shrink-0 rounded p-0.5 hover:bg-accent"
            // The row opens the pull request; the glyph opens what it is reporting.
            onClick={(event) => event.stopPropagation()}
            type="button"
          />
        }
      >
        <presentation.Icon aria-hidden className={presentation.toneClassName + " size-3.5"} />
      </MenuTrigger>
      <MenuPopup align="end" side="bottom" className="max-w-96">
        <MenuGroup>
          <MenuGroupLabel>{presentation.label}</MenuGroupLabel>
          {open ? (
            <ChecksBody
              environmentId={environmentId}
              host={host}
              number={number}
              projectId={projectId}
              repository={repository}
            />
          ) : null}
        </MenuGroup>
      </MenuPopup>
    </Menu>
  );
}
